// GameObject and Component type definitions

export type GameObjectType =
  | 'empty'
  | 'sprite'
  | 'container'
  | 'text'
  | 'graphics'
  | 'particle'
  | 'camera'
  | 'light'
  | 'spine'
  | 'cascade-slot'
  | 'backend-slot-game';

export type ComponentType =
  | 'transform'
  | 'sprite-renderer'
  | 'text-renderer'
  | 'graphics-renderer'
  | 'camera'
  | 'light'
  | 'box-collider'
  | 'circle-collider'
  | 'polygon-collider'
  | 'rigidbody'
  | 'audio-source'
  | 'animator'
  | 'particle-system'
  | 'spine-renderer'
  | 'spine-animator'
  // Cascade Slot
  | 'cascade-slot-config'
  | 'cascade-slot-part'
  // Backend Game
  | 'backend-game-config';

export interface Transform {
  position: { x: number; y: number };
  rotation: number; // degrees
  scale: { x: number; y: number };
  anchor: { x: number; y: number };
  sortOrder: number; // render order — higher values render on top (default: 0)
}

export interface Component {
  id: string;
  type: string;
  enabled: boolean;
  data: Record<string, unknown>;
}

export interface SpriteRendererData {
  textureId: string | null;
  texturePath: string | null;
  tint: string; // hex color
  alpha: number;
  flipX: boolean;
  flipY: boolean;
  sortingLayer: string;
  sortingOrder: number;
}

export interface BoxColliderData {
  isTrigger: boolean;
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
  showInScene: boolean;
}

export interface CircleColliderData {
  isTrigger: boolean;
  radius: number;
  offsetX: number;
  offsetY: number;
  showInScene: boolean;
}

export interface ScriptComponentData {
  scriptPath: string;
  scriptName: string;
  exposedProperties: ExposedProperty[];
}

export interface ExposedProperty {
  name: string;
  type: 'number' | 'string' | 'boolean' | 'reference';
  value: unknown;
  min?: number;
  max?: number;
}

export interface GameObject {
  id: string;
  name: string;
  type: GameObjectType;
  tag: string;
  layer: string;
  active: boolean;
  visible: boolean;
  locked: boolean;
  expanded: boolean;
  parentId: string | null;
  childIds: string[];
  transform: Transform;
  components: Component[];
}

export const DEFAULT_TRANSFORM: Transform = {
  position: { x: 0, y: 0 },
  rotation: 0,
  scale: { x: 1, y: 1 },
  anchor: { x: 0.5, y: 0.5 },
  sortOrder: 0,
};

export const GAMEOBJECT_ICONS: Record<GameObjectType, string> = {
  empty: '📦',
  sprite: '🖼️',
  container: '📁',
  text: '📝',
  graphics: '🎨',
  particle: '✨',
  camera: '📷',
  light: '💡',
  spine: '🎭',
  'cascade-slot': '🎰',
  'backend-slot-game': '🎮',
};

export const DEFAULT_TAGS = [
  'Untagged',
  'Player',
  'Enemy',
  'MainCamera',
  'Respawn',
  'Finish',
  'EditorOnly',
  'GameController',
];

export const DEFAULT_LAYERS = [
  'Default',
  'TransparentFX',
  'IgnoreRaycast',
  'Water',
  'UI',
  'Background',
  'Foreground',
];
